import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/SessionContext';
import { useBrowserAuth } from '../context/AuthContext';
import { recordPortalRedirectDiagnostic } from '../services/browserAuthTransport';
import { logger } from '../utils/logger';
import { useTranslation } from '../localization/useAppTranslation';
import { LoadingScreen } from './LoadingScreen';
import { ServerErrorScreen } from './ServerErrorScreen';

const moduleLogger = logger.getModule('AuthGuard');

interface AuthGuardProps {
  children: React.ReactNode;
}

const SENSITIVE_RETURN_PARAMETERS = new Set([
  'token',
  'jwt',
  'csrf',
  'csrftoken',
  'session_id',
  'web_session_id',
]);
const PORTAL_REDIRECT_ATTEMPT_KEY = 'sg-coach-portal-redirect-attempt';
const PORTAL_REDIRECT_LOOP_WINDOW_MS = 60_000;

export const claimPortalRedirectAttempt = (
  storage: Storage,
  currentTime = Date.now(),
): boolean => {
  try {
    const previousAttempt = Number(storage.getItem(PORTAL_REDIRECT_ATTEMPT_KEY));
    const elapsedTime = currentTime - previousAttempt;
    const isRecentAttempt = previousAttempt > 0
      && elapsedTime >= 0
      && elapsedTime < PORTAL_REDIRECT_LOOP_WINDOW_MS;

    if (isRecentAttempt) {
      return false;
    }

    storage.setItem(PORTAL_REDIRECT_ATTEMPT_KEY, String(currentTime));
    return true;
  } catch {
    return true;
  }
};

const clearPortalRedirectAttempt = (storage: Storage): void => {
  try {
    storage.removeItem(PORTAL_REDIRECT_ATTEMPT_KEY);
  } catch {
    // Storage can be unavailable in restricted browser contexts.
  }
};

export const buildPortalLoginUrl = (portalLoginUrl: string, currentUrl: string): string => {
  const returnUrl = new URL(currentUrl);
  Array.from(returnUrl.searchParams.keys()).forEach((parameterName) => {
    if (SENSITIVE_RETURN_PARAMETERS.has(parameterName.toLowerCase())) {
      returnUrl.searchParams.delete(parameterName);
    }
  });

  const loginUrl = new URL(portalLoginUrl, returnUrl.origin);
  loginUrl.searchParams.set('redirect_to', returnUrl.toString());
  return loginUrl.toString();
};

export const AuthGuard: React.FC<AuthGuardProps> = ({ children }) => {
  const { t } = useTranslation('auth');
  const browserAuth = useBrowserAuth();
  const { authState, session, sessionError, logout, retryCreateSession } = useAuth();
  const redirectStartedRef = useRef(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isRetryingBrowserAuth, setIsRetryingBrowserAuth] = useState(false);
  const [redirectLoopDetected, setRedirectLoopDetected] = useState(false);

  useEffect(() => {
    if (browserAuth.authenticated) {
      clearPortalRedirectAttempt(window.sessionStorage);
      redirectStartedRef.current = false;
      setRedirectLoopDetected(false);
      return;
    }

    if (
      !browserAuth.loading
      && !browserAuth.authenticated
      && browserAuth.portalLoginUrl
      && !browserAuth.error
    ) {
      if (redirectStartedRef.current) {
        return;
      }

      if (!claimPortalRedirectAttempt(window.sessionStorage)) {
        moduleLogger.error('Stopped repeated Portal authentication redirect');
        setRedirectLoopDetected(true);
        return;
      }

      redirectStartedRef.current = true;
      const loginUrl = buildPortalLoginUrl(browserAuth.portalLoginUrl, window.location.href);
      moduleLogger.info('Redirecting to Portal for sign-in (with redirect_to)', { loginUrl });
      recordPortalRedirectDiagnostic('retry-redirect', loginUrl);
      window.location.assign(loginUrl);
    }
  }, [
    browserAuth.authenticated,
    browserAuth.error,
    browserAuth.loading,
    browserAuth.portalLoginUrl,
  ]);

  // Handle retry for session creation errors
  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await retryCreateSession();
    } catch (error) {
      moduleLogger.error('Retry failed', error);
    } finally {
      setIsRetrying(false);
    }
  };

  const handleBrowserAuthRetry = async () => {
    setIsRetryingBrowserAuth(true);
    clearPortalRedirectAttempt(window.sessionStorage);
    redirectStartedRef.current = false;
    setRedirectLoopDetected(false);
    try {
      await browserAuth.retry();
    } catch (error) {
      moduleLogger.error('Browser authentication retry failed', error);
    } finally {
      setIsRetryingBrowserAuth(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    await logout();
  };

  // Show loading screen during initial auth check or session validation
  if (authState.isLoading) {
    return <LoadingScreen />;
  }

  const browserAuthenticationError = browserAuth.error || (
    redirectLoopDetected ? t('guard.redirectLoopDetected') : null
  );

  if (!browserAuth.authenticated && browserAuthenticationError) {
    const isNetworkError = /connection|network|reach|timed out|failed to fetch/i.test(
      browserAuthenticationError
    );

    return (
      <ServerErrorScreen
        error={{
          message: browserAuthenticationError,
          isNetworkError,
          isServerError: !isNetworkError,
          timestamp: Date.now(),
        }}
        onRetry={handleBrowserAuthRetry}
        isRetrying={isRetryingBrowserAuth}
      />
    );
  }
  
  // Browser authentication is owned by the Portal; wait for the redirect.
  if (!authState.isAuthenticated || !authState.user) {
    return <LoadingScreen message={t('guard.redirectingToSignIn')} />;
  }

  // Show error screen if session creation failed with network error
  if (sessionError) {
    return (
      <ServerErrorScreen 
        error={sessionError}
        onRetry={handleRetry}
        onLogout={handleLogout}
        isRetrying={isRetrying}
      />
    );
  }

  // Authenticated but no session yet - show loading screen
  if (!session) {
    return <LoadingScreen message={t('guard.creatingSession')} />;
  }
  
  return <>{children}</>;
};
